import React from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { ChartDataResponse } from "../ceo-dashboard.service";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrBefore);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface InflowsOutflowsChartProps {
  chartData: ChartDataResponse | null;
  loading: boolean;
  dateRange: [Dayjs | null, Dayjs | null];
}

const InflowsOutflowsChart: React.FC<InflowsOutflowsChartProps> = ({
  chartData,
  loading,
  dateRange,
}) => {
  // Generate all months in the selected range
  const generateMonthsRange = () => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) return [];

    const months: string[] = [];
    let current = startDate.clone();

    while (current.isSameOrBefore(endDate, "month")) {
      months.push(current.format("MMM YYYY"));
      current = current.add(1, "month");
    }

    return months;
  };

  // Process chart data for inflows and outflows
  const processChartData = () => {
    if (!chartData?.data) return null;

    const allMonths = generateMonthsRange();
    const groupedData: { [key: string]: { [key: string]: number } } = {};

    // Initialize all months with zero values
    allMonths.forEach((month) => {
      groupedData[month] = {
        Inflow: 0,
        Outflow: 0,
        "Net Flow": 0,
      };
    });

    // Process actual data
    chartData.data.forEach((item) => {
      const apiMonth = item.month_name; // Format: "August 2025"
      const amount = parseFloat(item.amount_kwd);

      // Convert API month format to chart format for matching
      const chartMonth = dayjs(apiMonth, "MMMM YYYY").format("MMM YYYY"); // "Aug 2025"

      if (allMonths.includes(chartMonth)) {
        if (item.trx_type === "Deposit" || item.trx_type === "Subscription") {
          groupedData[chartMonth]["Inflow"] += amount;
        } else if (
          item.trx_type === "Withdrawal" ||
          item.trx_type === "Redemption"
        ) {
          groupedData[chartMonth]["Outflow"] += amount;
        }
      }
    });

    // Calculate Net Flow for each month
    allMonths.forEach((month) => {
      groupedData[month]["Net Flow"] =
        groupedData[month]["Inflow"] - groupedData[month]["Outflow"];
    });

    const datasets = [
      {
        label: "Inflow",
        type: "bar" as const,
        data: allMonths.map((month) => groupedData[month]["Inflow"]),
        backgroundColor: "#E4D249",
        borderColor: "#E4D249",
        borderWidth: 1,
        yAxisID: "y",
      },
      {
        label: "Outflow",
        type: "bar" as const,
        data: allMonths.map((month) => groupedData[month]["Outflow"]),
        backgroundColor: "#6FD195",
        borderColor: "#6FD195",
        borderWidth: 1,
        yAxisID: "y",
      },
      {
        label: "Net Flow",
        type: "line" as const,
        data: allMonths.map((month) => groupedData[month]["Net Flow"]),
        borderColor: "#4379EE",
        backgroundColor: "transparent",
        borderWidth: 3,
        pointBackgroundColor: "#4379EE",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4,
        fill: false,
        yAxisID: "y",
      },
    ];

    return {
      labels: allMonths,
      datasets,
    };
  };

  const chartOptions: ChartOptions<"bar" | "line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
        position: "bottom" as const,
        align: "center" as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            family: "Montserrat",
            size: 12,
          },
          generateLabels: function (chart) {
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);

            // Custom point styles for mixed chart
            labels.forEach((label) => {
              if (label.text === "Net Flow") {
                label.pointStyle = "line";
              } else {
                label.pointStyle = "rect";
              }
            });

            return labels;
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${parseFloat(
              context.parsed.y.toString()
            ).toLocaleString()} KWD`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            family: "Montserrat",
            size: 11,
          },
          color: "#6b7280",
          maxRotation: 45,
        },
      },
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        beginAtZero: true,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            family: "Montserrat",
            size: 11,
          },
          color: "#6b7280",
          callback: function (value) {
            return parseFloat(value.toString()).toLocaleString() + "K";
          },
        },
      },
    },
  };

  const processedChartData = processChartData();

  return (
    <div className="w-full lg:flex-1 inline-flex flex-col justify-start items-start shadow-md bg-white p-5 rounded-lg gap-5 lg:h-[420px] min-h-[300px]">
      <div className="self-stretch inline-flex justify-between items-center">
        <div className="justify-start text-zinc-800 text-xs font-semibold font-['Montserrat']">
          Monthly Inflows and Outflows
        </div>
      </div>
      <div className="self-stretch flex-1 bg-white flex flex-col justify-start items-start gap-2.5">
        <div className="self-stretch flex-1 relative min-h-[300px]">
          {processedChartData ? (
            <Chart
              type="bar"
              data={processedChartData}
              options={chartOptions}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              {loading ? "Loading chart..." : "No data available"}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-5">
        <div className="flex items-center">
          <div className="w-4 h-4 relative">
            <div className="w-px h-px left-[8px] top-[8px] absolute">
              <div className="w-2 h-2 left-[-4px] top-[-4px] absolute bg-amber-300 border border-white" />
            </div>
          </div>
          <span className="justify-start text-black/70 text-xs font-normal font-['Inter']">
            Inflows
          </span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-px h-px relative">
            <div className="w-2 h-2 left-[-4px] top-[-4px] absolute bg-emerald-300 border border-white" />
          </div>
          <span className="justify-start text-black/70 text-xs font-normal font-['Inter']">
            Outflows
          </span>
        </div>
        <div className="flex items-center gap-2">
          <img src="/images/deposit-node.svg" alt="Net Flow" />
          <span className="justify-start text-black/70 text-xs font-normal font-['Inter']">
            Net Flow
          </span>
        </div>
      </div>
    </div>
  );
};

export default InflowsOutflowsChart;
